import { useConversationLayout } from "./studio/useConversationLayout";
import { useContentMotion } from "./lib/useContentMotion";
import { navigateWorkbench, studioFrame as isStudioFrame, useWorkspaceNavigationWake } from "./lib/studioNavigation";
import { InteractionShelf } from "./panel/InteractionFrame";
import { conversationOpening } from "./lib/workspaces";
import { SurfaceConversationFocus } from "./panel/SurfaceIntent";
import type { CSSProperties } from "react";
import { PresentationShelf } from "./panel/PresentationShelf";
import { MainPanelPresence, MainPanelVisibility, useMainPanels } from "./panel/MainPanelPresence";
/** Studio routes and connection state.
 * The path selects an Agent instance; the hash selects its current page.
 * Chrome is presentational; stream and visibility lifecycles stay here. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  api,
  hubAgents,
  openStream,
  type Bootstrap,
  type Connection,
  type HubAgent,
  type PanelEntry,
  type PanelPresentation,
  type OwnerState,
  type PanelProblem,
  type SpendAlert,
  type RecordPanel,
} from "./lib/api";
import { AGENT_ID, decodePart } from "./lib/base";
import { chatFeed } from "./lib/chatFeed";
import { commandFeed } from "./lib/commandFeed";
import { settingsFeed } from "./lib/settingsFeed";
import { workFeed } from "./lib/workFeed";
import { approvalsFeed, usePendingApprovals } from "./lib/approvalsFeed";
import { useScreenReport } from "./lib/screen";
import { Toast, type ToastMessage } from "./hub/Toast";
import { ChatPage } from "./pages/ChatPage";
import { ApprovalsPage } from "./pages/ApprovalsPage";
import { SetupPage } from "./pages/SetupPage";
import { CapabilitiesPage } from "./pages/CapabilitiesPage";
import { TimelinePage } from "./pages/TimelinePage";
import { LedgerPage } from "./pages/LedgerPage";
import { AgentHome } from "./studio/AgentHome";
import { OwnerConversationDock } from "./studio/OwnerConversationDock";
import { IdentityPage } from "./studio/IdentityPage";
import { RequestSent } from "./studio/RequestSent";
import type { Theme } from "./lib/appearance";
import { AgentWorkspace } from "./studio/AgentWorkspace";
import { ConnectionsPage } from "./connections/ConnectionsPage";
import { connections } from "./connections/api";
import { ModelSourceSettings } from "./hub/ModelSourceSettings";
import { APPEARANCE_SETTING } from "./connections/WorkbenchSettings";
import { connectionHref, connectionSession } from "./connections/navigation";
import { useConversationDrawn } from "./lib/conversations";
import { SettingsDirectory } from "./connections/context";
import { settingsDestinations } from "./connections/directory";
import { SurfaceWorkspace } from "./panel/SurfaceWorkspace";
import { SurfaceIntentComposer, type SurfaceIntentDraft } from "./panel/SurfaceIntent";
import { PanelView } from "./panel/PanelView";
import { RecordPanels } from "./panel/records";
import {
  SurfaceVersions,
  SurfaceVisibility,
  PanelDeveloper,
} from "./panel/SurfaceHost";
import { DeveloperView, ErrorState } from "./shell/ui";
import { Icon } from "./components/Icon";
import { LoadingShell } from "./shell/LoadingShell";
import { AgentStartButton, StoppedAgent, agentNotRunning } from "./shell/StoppedAgent";
import {
  AgentBar,
  StatusPill,
  ThemeSwitch,
  useOuterNavButton,
  type StatusTone,
} from "./shell/StudioChrome";
import { useWorkToolsState, WorkToolsProvider } from "./shell/workTools";
import { Composer, type MessageOrigin } from "./shell/Composer";
import { OwnerNavigation, type OwnerDestination } from "./shell/OwnerNavigation";
import { ownerPending } from "./shell/ownerShell";
import { readDraft, useDraft, writeDraft } from "./lib/drafts";
import { formatMoney } from "./lib/money";
import { rememberWorkspace } from "./lib/lastWorkspace";
import { panelHref, sameWorkspace, workspaceEntries, workspaceHref, workspaceMemory, workspaceTitle } from "./lib/workspaces";
import { surfaceRequests, type SurfacePage } from "./lib/surfaceRequests";
import { screenPageLabel } from "./studio/WorkContext";
import { instanceStatus, wakesOnDemand, type Account } from "./hub/api";

type Route =
  | { kind: "today" }
  | { kind: "missing" }
  | { kind: "identity" }
  | { kind: "connections"; namespace?: string; session?: string }
  | { kind: "workspace"; create?: boolean; session?: string }
  | { kind: "chat"; session?: string }
  | { kind: "timeline" }
  | { kind: "approvals"; session?: string }
  | { kind: "capabilities" }
  | { kind: "ledger"; name: string }
  | { kind: "panel"; key: string; context?: Record<string, string>;
      /** An in-page save moves the address without replacing this visit's document or draft scope. */
      entry?: { context: Record<string, string>; visit: number } };

/**
 * A hash this shell knows, or 今天.
 *
 * The default moved from `chat` to `today` (mvp-plan #5). Everything that used
 * to answer a hash still answers it, so a bookmark on `#/chat` or
 * `#/panel/<key>` is unchanged; what changed is where an owner lands with no
 * hash at all.
 */
function routeFromHash(hash: string): Route {
  const [path, query] = hash.replace(/^#\/?/, "").split("?", 2);
  if (path.startsWith("ledger/"))
    return { kind: "ledger", name: path.slice(7) };
  if (path.startsWith("panel/")) {
    // A malformed key stays as written: no panel has it, so the page says it is not there.
    const key = decodePart(path.slice(6)) ?? path.slice(6);
    return {
      kind: "panel",
      key,
      // A bare tree link restores this Agent's own view, never the source
      // Agent's session or artifact. Explicit query context takes precedence.
      context: query === undefined
        ? readDraft(`navigation:panel:${key}`, {})
        : Object.fromEntries(new URLSearchParams(query)),
    };
  }
  if (path === "ip") return { kind: "identity" };
  // `#/prefs` was a second settings page with a different assembly; its
  // address still opens the one.
  if (path === "connections" || path === "prefs") return {
    kind: "connections",
    namespace: new URLSearchParams(query).get("namespace") ?? undefined,
    session: connectionSession(hash, "") || undefined,
  };
  if (path === "workspace") {
    const params = new URLSearchParams(query);
    return { kind: "workspace", create: params.get("create") === "1", session: params.get("session") || undefined };
  }
  if (path === "chat") {
    const params = new URLSearchParams(query);
    return { kind: "chat", session: params.get("session") ?? undefined };
  }
  if (path === "timeline") return { kind: "timeline" };
  if (path === "approvals") return { kind: "approvals", session: connectionSession(hash, "") || undefined };
  if (path === "capabilities") return { kind: "capabilities" };
  return { kind: !path || path === "today" ? "today" : "missing" };
}

/**
 * The ones an owner is shown. Everything else is behind the 开发者 switch.
 *
 * Read when the page is *drawn*, not only when the switch is turned off. It
 * used to be read in two places that both assumed the switch: `saveDeveloper`
 * brought a reader back from a developer page as it closed, and a CSS class.
 * Nothing asked the question on entry, so an owner who typed `#/timeline` got
 * the ledger's own filter row -- 「L0」, 「注册史」, a principal id and a
 * session key -- and `#/ledger/l0_events` got a page whose title is its table's
 * name (D677). A page an Owner can reach and cannot read is not an Owner page,
 * and a page nothing leads to is not a page: both halves of the same rule.
 */
const OWNER_ROUTES = new Set<Route["kind"]>([
  "today",
  "missing",
  "identity",
  "chat",
  "connections",
  "workspace",
  "approvals",
  "panel",
]);

function routeToHash(route: Route): string {
  switch (route.kind) {
    case "identity":
      return "#/ip";
    case "connections":
      return connectionHref(route.namespace, route.session ?? "s-owner");
    case "workspace": {
      const params = new URLSearchParams({ ...(route.create ? { create: "1" } : {}), ...(route.session ? { session: route.session } : {}) });
      return "#/workspace" + (params.size ? `?${params}` : "");
    }
    case "chat": {
      const params = new URLSearchParams(route.session ? { session: route.session } : {});
      return "#/chat" + (params.size ? `?${params}` : "");
    }
    case "timeline":
      return "#/timeline";
    case "approvals":
      return "#/approvals" + (route.session ? `?${new URLSearchParams({ session: route.session })}` : "");
    case "capabilities":
      return "#/capabilities";
    case "ledger":
      return `#/ledger/${route.name}`;
    case "panel":
      return `#/panel/${encodeURIComponent(route.key)}${route.context && Object.keys(route.context).length ? "?" + new URLSearchParams(route.context) : ""}`;
    default:
      return "#/today";
  }
}

/**
 * Which global destination the page in front of the owner belongs to.
 *
 * 「你在哪儿」 used to be answered for two pages out of the five: `active` was
 * passed only for 设置, so standing on 今天, 身份与关联账号 or 待我回应 lit
 * nothing at all (the report's D293). The rule is the plain one -- a
 * destination marks itself when the page it leads to is the page showing -- and
 * where each one leads depends on whether a Hub sits above this Studio. With
 * one, 总览 and 人和 IPs are the Hub's own pages and an Agent's own work is
 * never one of them; being inside an Agent is what the tree marks, and which
 * Agent is the tree's own job. Without one, all five lead here
 * (`OwnerNavigation`'s `local` branch) and each marks its own page.
 *
 * The pages inside an Agent -- 工作空间、对话, a plugin's work
 * surface -- are not top-level destinations in either shape. The Agent tree is
 * what says where the owner is standing on those.
 */
export function ownerDestination(
  kind: Route["kind"],
  hubDirectory: boolean,
): OwnerDestination | undefined {
  if (kind === "connections") return "settings";
  if (kind === "approvals") return "responses";
  if (hubDirectory) return undefined;
  if (kind === "identity") return "people";
  if (kind === "today") return "overview";
  return undefined;
}

/**
 * Where the Owner was standing, for the message they are about to send.
 *
 * The same fact s2 records for a page request (`submit_message(request=)`, with
 * its `panel_key`): a sentence typed while looking at a work surface is not the
 * same sentence typed on 今天, and the plugin reading the turn is the only one
 * that can tell what to do about it. The route's own word and, on a surface,
 * that surface's key -- nothing about the plugin, nothing the shell made up,
 * and none of it drawn on the page.
 */
export function messageOrigin(route: Route): MessageOrigin {
  return route.kind === "panel"
    ? { page: "panel", panel_key: route.key }
    : { page: route.kind };
}

function routeConversation(route: Route): string | undefined {
  return route.kind === "panel" ? route.context?.session || route.context?.session_id
    : "session" in route ? route.session : undefined;
}

/**
 * Whether the stage and the conversation fit side by side. Narrower than this,
 * they take turns on one screen (`styles/product-workspace.css`, 760px).
 */
const sideBySide = () => window.matchMedia?.("(min-width: 761px)").matches ?? true;

export default function App({ account = null, appearance }: {
  account?: Account | null;
  /** The studio's one appearance choice; `RootApp` holds it (`lib/appearance.ts`). */
  appearance?: { value: Theme; onChange: (theme: Theme) => void };
}) {
  const embeddedMode = new URLSearchParams(location.search).get("workspace");
  const studioFrame = isStudioFrame();
  const outerNavButton = useOuterNavButton(studioFrame);
  const settingsEmbedded = embeddedMode === "settings";
  const [openingSettings] = useState(() => {
    if (!settingsEmbedded) return undefined;
    // The authenticated read is independent of bootstrap. The server still
    // checks the current seat; failures are handled when the settings mount.
    const read = connections.directory();
    void read.catch(() => {});
    return read;
  });
  const embedded = embeddedMode === "embedded" || settingsEmbedded;
  // `workspace=embedded` is `IndependentAgent.tsx`'s frame, mounted only by
  // `AgentParallel` (并排工作) -- and that page puts no ceiling on how many of
  // an owner's Agents can be selected (`AgentCatalog.tsx`'s button only
  // requires >=2). Every one of them notifies `visible: true` at once, so a
  // stream per visible workspace (below) would ask this one HTTP/1 origin for
  // as many connections as Agents selected. `workspace=settings` never shows
  // more than one instance at a time (`OwnerSettings.tsx`'s
  // `AgentSettingsFrame` hides every agent but the selected one), so only
  // this one mode stays on the polling fallback.
  const parallelEmbedded = embeddedMode === "embedded";
  const [workspaceExpanded, setWorkspaceExpanded] = useState(!studioFrame);
  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState !== "hidden",
  );
  const workspaceVisible = workspaceExpanded && documentVisible;
  const wasHidden = useRef(false);
  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [bootError, setBootError] = useState<unknown>(null);
  const [startError, setStartError] = useState<unknown>(null);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [browserLink, setBrowserLink] = useState(true);
  const linkWasDown = useRef(false);
  /**
   * The stream came back after a gap, and what it would have said meanwhile is
   * unknown: the conversations read once, as soon as the owner is known again
   * -- not before, which would read a record still being replayed.
   */
  const owedRead = useRef(false);
  // What `told` (`liquid-page-runtime.md` §1's fallback-read rule) read last
  // time the fallback poll below looked. `null` until the first poll answers,
  // so that first answer is only remembered, never compared against nothing.
  const lastTold = useRef<number | null>(null);
  const [panels, setPanels] = useState<PanelEntry[]>([]);
  const [surfacePages, setSurfacePages] = useState<SurfacePage[]>([]);
  // Registrations remain authoritative; an older page read cannot revive a withdrawn generation.
  const navigationPanels = useMemo(() => {
    const origins = new Map(surfacePages.map(page => [page.key, page]));
    return panels.map(panel => {
      const page = origins.get(panel.key);
      return page && page.generation_id === panel.generation_id ? { ...panel, origin: page.origin } : panel;
    });
  }, [panels, surfacePages]);
  const [presentations, setPresentations] = useState<PanelPresentation[]>([]);
  const mainPanelsPresent = useMainPanels();
  const [problems, setProblems] = useState<PanelProblem[]>([]);
  const [recordPanels, setRecordPanels] = useState<RecordPanel[]>([]);
  // What is waiting for the person, off the one list the server pushes
  // (§7.194 ⑤). The badge used to keep its own count and fetch the list
  // again on every frame that already carried it.
  const pendingApprovals = usePendingApprovals();
  const pendingCount = pendingApprovals.pending?.length ?? 0;
  const [ownerReading, setOwnerReading] = useState<OwnerState | null>(null);
  const [agents, setAgents] = useState<HubAgent[] | null>(null);
  const [hostAgent, setHostAgent] = useState<HubAgent | null>(null);
  // The parent already follows the Hub directory; stale bootstrap/transport snapshots cannot override it.
  const directoryRow = hostAgent ?? agents?.find(agent => agent.id === AGENT_ID) ?? null;
  const ownerDisabled = Boolean(directoryRow && (directoryRow.location === "cloud" || directoryRow.client_id) && directoryRow.desired_running === false);
  const sleeping = !directoryRow?.online && wakesOnDemand(directoryRow) && !directoryRow?.runner?.last_error
    && ["sleeping", "stopping"].includes(directoryRow?.runtime?.state ?? "");
  const runtimePaused = ownerDisabled || sleeping;
  const starting = !directoryRow?.online && !ownerDisabled && !directoryRow?.runner?.last_error
    && ["starting", "restarting"].includes(directoryRow?.runtime?.state ?? directoryRow?.runner?.state ?? "");
  const runtimeNotice = runtimePaused || starting;
  const ownerReadingRef = useRef<OwnerState | null>(null);
  const [lastOwner, setLastOwner] = useState<OwnerState | null>(null);
  // Keep the already-open view during replay. Only the current read permits writes.
  const setOwner = useCallback((next: OwnerState) => {
    ownerReadingRef.current = next;
    setOwnerReading(next);
    if (next.present !== null) setLastOwner(next);
  }, []);
  const owner = ownerReading?.present === null && lastOwner?.present === true ? lastOwner : ownerReading;
  const ownerRecovering = ownerReading?.present === null;
  const workReady = ownerReading?.present === true && connection?.state === "ready" && browserLink
    && (!directoryRow || directoryRow.online) && !runtimePaused;
  /**
   * Nothing of this Agent's own can be drawn yet.
   *
   * The loading feedback's condition, named once rather than written twice, because
   * the stopped-Agent page below is the same question answered from the other
   * side: if this is false there is work on the screen and nothing to explain,
   * and if it is true the reader is owed a reason (D681).
   *
   * A *failed* bootstrap is only one of the ways to get here, and it is not the
   * one the walk measured. A stopped cloud Agent still has a desk on the Hub --
   * `hub/directory.py::_desk_here` builds one for any Agent that exists, and its
   * projection answers `/api/bootstrap` with a 200 -- so `boot` arrives and
   * `owner.present` is null for as long as the socket is missing
   * (`workbench/server/service.py::owner_state`). That is exactly the 25 seconds
   * of 「正在恢复这个 IP 的归属…」 rows zz02-zz05 photographed.
   */
  const agentUnread = !boot || !connection || !owner || owner.present === null;
  const [spend, setSpend] = useState<SpendAlert | null>(null);
  const [capabilityVersion, setCapabilityVersion] = useState(0);
  /** Panel key -> the signature of its `ui/` tree, pushed by the server. */
  const [surfaceVersions, setSurfaceVersions] = useState<
    Record<string, string>
  >({});
  const [route, setRoute] = useState<Route>(() => {
    const initial = routeFromHash(location.hash);
    return initial.kind === "panel" ? { ...initial, entry: { context: initial.context ?? {}, visit: 0 } } : initial;
  });
  const panelVisit = useRef(0);
  const requestedRoutes = useRef<{ hash: string; route: Route }[]>([]);
  // A request's page goes when its answer is taken. Remembering that the page open was a request is what lets
  // that read as the end of a request, and not as a workspace that vanished.
  const openRequest = useRef<string | null>(null);
  // A request also goes when it expires or is answered or withdrawn elsewhere: only an answer this page
  // took makes its going read as sent.
  const answeredHere = useRef<string | null>(null);
  // Whether the owner could read the conversation the open request was raised in, as the page last had it:
  // the entry is gone by the time the end of the request is drawn.
  const requestReadable = useRef(true);
  const routeKey = route.kind === "panel" ? route.key : null;
  const routeEntry = routeKey === null ? undefined : panels.find(panel => panel.key === routeKey);
  if (routeEntry) {
    openRequest.current = routeEntry.interaction ? routeKey : null;
    requestReadable.current = routeEntry.interaction?.owner_conversation !== false;
  } else if (openRequest.current !== routeKey) openRequest.current = null;
  if (answeredHere.current !== routeKey) answeredHere.current = null;
  const requestEnded = routeKey !== null && !routeEntry && openRequest.current === routeKey;
  const requestSession = route.kind === "panel" ? route.context?.session_id ?? route.context?.session : undefined;
  /**
   * Go to a page of this Agent.
   *
   * In a frame the Hub routes, the Hub carries it out and sends the route
   * back; drawing the page here first would draw one the Hub may open
   * somewhere else -- an Agent's settings open in the Hub's 设置. `hash` is
   * the address as a card wrote it, when one did.
   */
  const go = useCallback((next: Route, replace = false, hash = routeToHash(next), keepPanelEntry = false) => {
    const target: Route = next.kind === "panel" ? { ...next, entry: keepPanelEntry
      ? next.entry ?? { context: next.context ?? {}, visit: 0 }
      : { context: next.context ?? {}, visit: ++panelVisit.current } } : next;
    // A Hub-managed frame receives the address back asynchronously. Keep the same visit when that address echoes.
    const request = { hash, route: target };
    requestedRoutes.current.push(request);
    if (!navigateWorkbench(hash, replace)) {
      requestedRoutes.current = requestedRoutes.current.filter(item => item !== request);
      setRoute(target);
    }
  }, []);
  const [storedSessionId, setSessionId] = useState(() => {
    const initial = routeFromHash(location.hash);
    // A page's conversation is taken once the console says the owner can read it (`followed` below).
    return (initial.kind === "panel" ? undefined : routeConversation(initial)) || readDraft("active-conversation", "s-owner");
  });
  const [conversationOverride, setConversationOverride] = useState<{ route: Route; session: string } | null>(null);
  // A page takes the docked conversation to the one it came from only once the console says the owner can read it.
  // One the Agent holds for itself (the operating loop's) shows nothing here: docked, the owner would read an empty
  // conversation and write into the Agent's own. Until the answer comes, the dock stays where it was.
  const pageConversation = route.kind === "panel" ? routeConversation(route) : undefined;
  const pageConversationDrawn = useConversationDrawn(pageConversation);
  const followed = route.kind === "panel" && pageConversationDrawn !== true ? undefined : routeConversation(route);
  const overrideRoute = conversationOverride?.route;
  const sameConversationVisit = overrideRoute === route || route.kind === "panel" && overrideRoute?.kind === "panel"
    && route.key === overrideRoute.key && route.entry !== undefined && route.entry.visit === overrideRoute.entry?.visit;
  const sessionId = conversationOverride && sameConversationVisit ? conversationOverride.session
    : followed || storedSessionId;
  /** The bar's own text box: what 「去说一句」 from a page focuses. */
  const conversationRef = useRef<HTMLElement>(null);
  const [chatCollapsed, setChatCollapsed] = useDraft("chat:collapsed", false);
  const chatLayout = useConversationLayout();
  const focusConversation = () => {
    setChatCollapsed(false);
    requestAnimationFrame(() => conversationRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus());
  };
  const conversationReturn = useRef<Route>({ kind: "workspace" });
  const [surfaceIntent, setSurfaceIntent] = useState<SurfaceIntentDraft | null>(null);
  // A shell mounted without one is a test rendering this component on its own;
  // the real document always has `RootApp` above it.
  const theme = appearance?.value ?? "system";
  // Diagnostics stay opt-in; the Owner's work and decisions come first.
  const [developer, setDeveloper] = useState(false);
  /** Whether `developer` above is this owner's saved answer or still the default. */
  const [developerRead, setDeveloperRead] = useState(false);
  const [chatVersion, setChatVersion] = useState(0);
  const [approvalVersion, setApprovalVersion] = useState(0);
  const [ledgerVersion, setLedgerVersion] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [navigationVersion, revealNavigation] = useState(0);
  // Moves when the Hub says this Agent's directory row changed (its name).
  const [directoryVersion, setDirectoryVersion] = useState(0);
  const panelSignature = JSON.stringify(panels.map(panel => [panel.key, panel.generation_id]).sort());
  const headingRef = useRef<HTMLDivElement>(null);
  useContentMotion(headingRef, route.kind === "panel" ? route.key : route.kind, workReady && workspaceVisible && route.kind !== "panel" && route.kind !== "connections");
  const ledgerTick = useRef(0);
  useLayoutEffect(() => {
    // An embedded workspace gates its own surface; its ordinary-message
    // composer can still hand text to the resident Hub while Runtime sleeps.
    headingRef.current?.toggleAttribute("inert", !parallelEmbedded && !developer && owner?.present === true && !workReady);
  }, [parallelEmbedded, developer, owner?.present, workReady]);

  const onToast = useCallback((message: string, tone: "good" | "critical") => {
    setToast({ message, tone });
  }, []);
  const dismissToast = useCallback(() => setToast(null), []);

  const foregroundRead = useRef<Promise<boolean> | null>(null);
  const load = useCallback((background = false, signal?: AbortSignal, onBody?: (body: Bootstrap) => void) => {
    const reading = api
      .bootstrap(signal, background)
      .then((body) => {
        if (signal?.aborted) return false;
        setBoot(old => body.owner.present === null && old ? old : body);
        setConnection(body.connection);
        if (body.owner.present !== null) {
          setPanels(body.panels);
          setPresentations(body.presentations ?? []);
          if (body.surface_versions) setSurfaceVersions(body.surface_versions);
          setRecordPanels(body.record_panels);
          setProblems(body.panel_problems);
        }
        setOwner(body.owner);
        if (body.owner.present !== null) {
          setSpend(body.spend);
          setDeveloper(body.prefs.developer === true);
          setDeveloperRead(true);
        }
        setBootError(null);
        if (body.connection.state === "ready" && body.owner.present !== null) setStartError(null);
        setBrowserLink(true);
        // A record still being replayed is not read back into the pages: the
        // owner coming back is when they read (the `owner` frame).
        if (background && body.owner.present !== null) {
          owedRead.current = false;
          setChatVersion((n) => n + 1);
          setLedgerVersion((n) => n + 1);
        }
        onBody?.(body);
        return true;
      })
      .catch((err) => {
        if (!signal?.aborted) {
          if (background) setBrowserLink(false);
          else setBootError(err);
        }
        return false;
      });
    if (!background) {
      foregroundRead.current = reading;
      void reading.then(() => { if (foregroundRead.current === reading) foregroundRead.current = null; });
    }
    return reading;
  }, [setOwner]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (settingsEmbedded && (bootError || boot?.owner.present === false))
      parent.postMessage({ type: "pa.settings.ready" }, location.origin);
  }, [settingsEmbedded, bootError, boot]);

  // Hidden tabs must release HTTP/1 stream slots used by the active workspace.
  // On return, the existing wake path reloads everything missed while hidden.
  useEffect(() => {
    const changed = () =>
      setDocumentVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);

  // Which pages are in front of the owner is the shell's to say: a hidden tab,
  // or a frame whose workspace the page around it folded away, has none.
  const screenViews = useScreenReport(workspaceVisible, sessionId, route.kind === "panel"
    ? { panel_key: route.key, context: route.entry?.context ?? route.context ?? { session_id: sessionId } } : null);

  // A live stream (below) is what keeps an embedded workspace current now,
  // for every mode except `parallelEmbedded` (see its declaration above).
  // This loop is what those other modes fall back to while the stream is
  // down -- not a clock that drives the page while it is up -- so a broken
  // link still converges without a second clock racing the stream once it
  // reconnects (`commandFeed.resync()` handles that moment instead).
  // `parallelEmbedded` has no stream to fall back from at all, so it keeps
  // polling on the clock exactly as before.
  //
  // A page here has no stream to hear a `command`/`work` frame directly, so
  // this loop is also the one place that watches `told` (`liquid-page-runtime.md`
  // §1's fallback-read rule) and raises the same `resync()` the stream's own
  // return raises, the moment `told` differs from what the previous poll saw
  // -- not only a larger value, since a restarted server counts from 0 again.
  // The first answer is just remembered: there is nothing to compare it to
  // yet. This never also publishes a `work` tick -- that would make the one
  // subscribed surface here read twice for a single change.
  useEffect(() => {
    if (!embedded || !workspaceVisible) return;
    if (!parallelEmbedded && browserLink !== false) return;
    let live = true;
    let timer = 0;
    let failures = 0;
    const controller = new AbortController();
    const poll = async () => {
      const okay = await load(true, controller.signal, (body) => {
        if (lastTold.current !== null && lastTold.current !== body.told) commandFeed.resync();
        lastTold.current = body.told;
      });
      failures = okay ? 0 : Math.min(failures + 1, 4);
      if (live)
        timer = window.setTimeout(poll, Math.min(30000, 2500 * 2 ** failures));
    };
    timer = window.setTimeout(poll, 2500);
    return () => {
      live = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [embedded, parallelEmbedded, workspaceVisible, browserLink, load]);

  useEffect(() => {
    if (!workspaceVisible) {
      wasHidden.current = true;
      return;
    }
    if (!wasHidden.current) return;
    wasHidden.current = false;
    load();
    setChatVersion((n) => n + 1);
    setLedgerVersion((n) => n + 1);
    setApprovalVersion((n) => n + 1);
    setCapabilityVersion((n) => n + 1);
  }, [workspaceVisible, load]);

  // A Hub answers with its directory; refresh again after the browser link
  // recovers, without starting a poll for every navigation node.
  //
  // Not asked at all on a single-agent `pa-workbench serve`. That deployment has
  // no directory and says so -- `/api/hub/agents` answers 404 with a sentence,
  // deliberately (`workbench/server/app.py`) -- but the question had already been
  // answered before this page loaded: `RootApp` read `/api/hub/me`, and a shell
  // mounted under a Hub is the only one that gets an account. Asking anyway put a
  // 404 in the log every time a surface opened (D431) and taught anybody reading
  // that log to ignore one.
  useEffect(() => {
    if (!account) {
      setAgents(null);
      return;
    }
    if (!workspaceVisible) return;
    let live = true;
    hubAgents()
      .then(rows => { if (live) setAgents(rows); })
      .catch(() => { if (live) setAgents(null); });
    return () => { live = false; };
  }, [account, browserLink, connection?.state, ownerReading?.present, directoryVersion, workspaceVisible]);
  // The directory answers for an Agent that cannot answer for itself, which is
  // the whole of D681: with no process there is nobody behind the desk to say
  // who owns this Agent, and waiting for that blocked four pages with loading feedback.
  const notRunning = agentUnread && agentNotRunning(directoryRow);

  const recoverWorkspace = useCallback(() => {
    if (!workspaceVisible) return;
    revealNavigation(version => version + 1);
    if (workReady || directoryRow?.desired_running === false || foregroundRead.current) return;
    // One foreground bootstrap uses the Hub's normal admission and Owner-off
    // checks. Background recovery remains Observe, and the seat stays closed
    // until this read or the stream confirms the connection and Owner again.
    void load();
  }, [workspaceVisible, workReady, directoryRow?.desired_running, load]);
  useWorkspaceNavigationWake(recoverWorkspace);

  // Recovery cannot depend on receiving one final SSE owner event: backlog
  // replay can leave the browser holding an earlier unknown snapshot even
  // after the server has caught up. Keep checking the authoritative bootstrap
  // while ownership is unknown or the Agent is stopped. Only one read at a
  // time; hidden pages pause and embedded pages already have their own poll.
  useEffect(() => {
    if (embedded || !workspaceVisible || (!notRunning && !ownerRecovering)) return;
    let live = true;
    let timer = 0;
    const controller = new AbortController();
    const again = async () => {
      await load(true, controller.signal);
      if (live) timer = window.setTimeout(again, 5000);
    };
    timer = window.setTimeout(again, 5000);
    return () => {
      live = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [notRunning, ownerRecovering, embedded, workspaceVisible, load]);

  useEffect(() => {
    const onHash = () => {
      const hash = location.hash;
      const requests = requestedRoutes.current;
      const matched = requests.findIndex(request => request.hash === hash);
      if (matched >= 0) {
        requestedRoutes.current = requests.slice(matched + 1);
        setRoute(requests[matched].route);
        return;
      }
      requestedRoutes.current = [];
      setRoute(current => {
        const address = routeToHash(current);
        if (hash === address || hash === `${address}?`) return current;
        const next = routeFromHash(hash);
        return next.kind === "panel" ? { ...next, entry: { context: next.context ?? {}, visit: ++panelVisit.current } } : next;
      });
    };
    addEventListener("hashchange", onHash);
    addEventListener("popstate", onHash);
    return () => { removeEventListener("hashchange", onHash); removeEventListener("popstate", onHash); };
  }, []);

  // A hash only the developer view answers, with the developer view off. The
  // address is replaced rather than pushed: there is no page behind it to go
  // back to, and an owner who pressed 返回 would be sent there again.
  //
  // Waits for the saved answer: `developer` starts false because that is what
  // this console is, and a reader whose switch is on would otherwise be thrown
  // off their own page in the moment before the bootstrap lands.
  useEffect(() => {
    if (!developerRead || developer || embedded || OWNER_ROUTES.has(route.kind)) return;
    go({ kind: "today" }, true);
  }, [developerRead, developer, embedded, route.kind, go]);

  useEffect(() => {
    if (!embedded && !studioFrame) return;
    const receive = (event: MessageEvent) => {
      if (event.source !== parent || event.origin !== location.origin) return;
      if (event.data?.type === "pa.workspace.visibility") setWorkspaceExpanded(Boolean(event.data.visible));
      else if (AGENT_ID && parent !== window && event.data?.type === "pa.workspace.agent" && event.data.instance?.id === AGENT_ID)
        setHostAgent(event.data.instance as HubAgent);
    };
    window.addEventListener("message", receive);
    // Whatever the Hub told this frame before now reached nobody; it tells
    // it again on hearing this (`useAgentFrame`).
    parent.postMessage({ type: "pa.studio.mounted" }, location.origin);
    return () => window.removeEventListener("message", receive);
  }, [embedded, studioFrame]);

  // A workspace that is visible holds one stream; a hidden one holds none
  // (`liquid-page-runtime.md` §1's "told, not asked"). `parallelEmbedded`
  // stays out: see its declaration above for why that one mode alone cannot
  // afford one stream per visible instance.
  useEffect(() => {
    if (!workspaceVisible || parallelEmbedded) return;
    return openStream(
      (event) => {
        switch (event.event) {
          case "connection":
            setConnection(event.data);
            if (event.data.state !== "ready") setPresentations([]);
            break;
          case "spend":
            setSpend(event.data);
            break;
          case "capabilities":
            setCapabilityVersion((n) => n + 1);
            break;
          case "surfaces":
            // A surface's own files changed on disk (§7.130 phase 2). Handed
            // straight down to whichever SurfaceHost is showing that panel;
            // nothing else in the shell has any use for it.
            setSurfaceVersions(event.data.versions);
            break;
          case "panels":
            if (ownerReadingRef.current?.present === null) break;
            setPanels(event.data.panels);
            setPresentations(event.data.presentations ?? []);
            setRecordPanels(event.data.record_panels ?? []);
            api
              .panels()
              .then((body) => setProblems(body.problems))
              .catch(() => {});
            break;
          case "approvals":
            approvalsFeed.publish(event.data.pending);
            setApprovalVersion((n) => n + 1);
            break;
          case "command":
            // What became of a submission (CHARTER §7.194). Handed to whichever
            // component is showing that command; the shell keeps no copy.
            commandFeed.publish(event.data);
            break;
          case "work":
            // Something other than looking happened somewhere in this Agent
            // (`liquid-page-runtime.md` §1). Carries no identity of what ran;
            // every subscribed surface's answer is the same, so it goes
            // straight to whichever pages are listening.
            workFeed.publish(event.data);
            break;
          case "owner":
            if (ownerReadingRef.current?.present === null && event.data.present === true) void load(true);
            else if (owedRead.current && event.data.present !== null) setChatVersion((n) => n + 1);
            if (event.data.present !== null) owedRead.current = false;
            setOwner(event.data);
            setApprovalVersion((n) => n + 1);
            break;
          case "chat":
            // Which conversation moved, or the one bubble a chunk moved. Each
            // mounted conversation decides whether it is its news; the shell
            // does not know which one is on screen, and does not guess.
            chatFeed.publish(event.data);
            break;
          case "ledger":
            // Ledger pages are tables of record, not tickers: coalesce.
            ledgerTick.current += 1;
            if (ledgerTick.current % 5 === 0) setLedgerVersion((n) => n + 1);
            break;
          case "directory":
            // Renamed -- by the owner elsewhere, or by the Agent itself once
            // its IP was settled. The header and navigation read the row again.
            setDirectoryVersion((n) => n + 1);
            break;
          case "settings":
            // Which namespace was written; whatever shows it reads it again.
            settingsFeed.publish(event.data);
            break;
          case "rebuilt":
            load();
            setLedgerVersion((n) => n + 1);
            onToast("它把自己的记录重读了一遍，这一页已经是最新的。", "good");
            break;
        }
      },
      (up) => {
        setBrowserLink(up);
        // A gap in the stream is a gap in what was pushed: every open command
        // reading fetches once more, then goes back to listening, and so does
        // every setting shown; the conversations read when the owner is known.
        if (up && linkWasDown.current) {
          commandFeed.resync();
          settingsFeed.resync();
          owedRead.current = true;
        }
        linkWasDown.current = !up;
      },
    );
  }, [load, onToast, workspaceVisible, parallelEmbedded]);

  const settingsDirectory = useMemo(() => settingsDestinations(panels), [panels]);
  const opening = conversationOpening(panels);

  useEffect(() => {
    if (embedded) return;
    headingRef.current
      ?.querySelector<HTMLElement>(".page-title, .own-title")
      ?.focus();
  }, [route.kind === "panel" ? `${route.key}:${route.entry?.visit ?? 0}` : route, embedded]);

  useEffect(() => {
    if (followed) setSessionId(followed);
  }, [route, followed]);
  useEffect(() => {
    if (route.kind !== "chat") conversationReturn.current = route;
  }, [route, sessionId]);

  useEffect(() => {
    try { writeDraft("active-conversation", sessionId); } catch { /* Draft input has its own visible save error. */ }
  }, [sessionId]);
  useEffect(() => {
    if (route.kind === "panel") {
      try { writeDraft(`navigation:panel:${route.key}`, route.context || {}); } catch { /* View hints are optional. */ }
      const entry = workspaceEntries(navigationPanels).find(panel => panel.key === route.key);
      if (entry) {
        try { writeDraft(`navigation:${workspaceMemory(entry)}`, route.context || {}); } catch { /* View hints are optional. */ }
      }
    }
    if (!["chat", "connections", "approvals"].includes(route.kind)) {
      try { writeDraft("navigation:work", routeToHash(route)); } catch { /* The current page still retains its view. */ }
    }
  }, [route, navigationPanels]);
  // An overview workspace the owner opens is the one this Agent opens on next time, when it has several.
  useEffect(() => {
    if (route.kind !== "panel") return;
    const opened = navigationPanels.find(panel => panel.key === route.key);
    if (opened?.overview === true) rememberWorkspace(opened);
  }, [route, navigationPanels]);
  // A template is replaced by the registered instance that took its place; a
  // support page opens as itself.
  //
  // The rule used to be "any panel in a workspace becomes that workspace's
  // entry", and `#/panel/producer_video.review` was therefore unreachable:
  // 看片 is `role: "support"`, so the owner typing its address -- or following a
  // link to it -- landed on the studio with nothing said (D423). Which panel a
  // local instance supersedes is the only question this redirect exists to
  // answer (`lib/workspaces.ts`), and only a template has a successor.
  useEffect(() => {
    if (route.kind !== "panel") return;
    const previous = panels.find(panel => panel.key === route.key);
    if (previous?.workspace?.role !== "template") return;
    const replacement = workspaceEntries(panels)
      .find(panel => panel.workspace?.key === previous.workspace?.key);
    if (!replacement || replacement.key === route.key) return;
    const next: Route = { ...route, key: replacement.key };
    go(next, true);
  }, [panels, route, go]);

  const navigate = (next: Route) => {
    recoverWorkspace();
    if ((next.kind === "connections" || next.kind === "approvals" || next.kind === "workspace") && !next.session) next = { ...next, session: sessionId };
    // The visible address owns its task coordinates (including a chat request); a session or last workspace does not.
    if (next.kind === "connections" && route.kind !== "connections") {
      go(next, false, connectionHref(next.namespace, next.session, location.hash));
      return;
    }
    go(next);
  };
  const rememberPanelContext = (key: string, context: Record<string, string>) => {
    const active = route.kind === "panel" && route.key === key ? route : null;
    const requested = requestedRoutes.current.at(-1)?.route;
    const previous = (active && requested?.kind === "panel" && requested.key === key
      && requested.entry?.visit === active.entry?.visit ? requested.context : active?.context) ?? {};
    const moved = active && (Object.keys(previous).length !== Object.keys(context).length
      || Object.entries(context).some(([name, value]) => previous[name] !== value));
    // A bare address restores a draft once. Before changing that draft, give the old history entry an explicit
    // coordinate (including an empty query), so Back reopens that visit instead of restoring the newest draft.
    if (moved && !location.hash.includes("?") && !requestedRoutes.current.some(request => request.route.kind === "panel"
      && request.route.key === active.key && request.route.entry?.visit === active.entry?.visit)) {
      const address = routeToHash(active);
      go(active, true, address.includes("?") ? address : `${address}?`, true);
    }
    const entry = workspaceEntries(navigationPanels).find(panel => panel.key === key);
    writeDraft(`navigation:panel:${key}`, context);
    // A support page owns its return position, never the workspace entry's object.
    if (entry) writeDraft(`navigation:${workspaceMemory(entry)}`, context);
    writeDraft("navigation:work", panelHref(key, context));
    if (active) {
      const next = { ...active, context };
      conversationReturn.current = next;
      if (moved) {
        const address = routeToHash(next);
        go(next, false, address.includes("?") ? address : `${address}?`, true);
      }
    }
  };
  const expandConversation = (source: Route = route) => {
    conversationReturn.current = source;
    navigate({ kind: "chat", session: sessionId });
  };
  const placeSurfaceIntent = (draft: SurfaceIntentDraft) => {
    if (!workReady) throw new Error("连接与管理身份确认后，才能准备工作请求。");
    setSurfaceIntent(draft);
    setSessionId(draft.session_id);
    setConversationOverride({ route, session: draft.session_id });
    focusConversation();
    if (developer || route.kind === "connections") {
      conversationReturn.current = route;
      navigate({ kind: "chat", session: draft.session_id });
    }
  };
  const surfaceIntentPlaced = useCallback((commandId: string) => {
    setSurfaceIntent(current => current?.command_id === commandId ? null : current);
  }, []);
  /**
   * The Owner pressed 打开 on a page the Agent showed: the page goes on the
   * main stage and the conversation stays beside it -- docked, if it was the
   * whole page. On a screen too narrow for both, the stage takes the screen
   * and the shell's 对话 button brings the conversation back.
   */
  const openPresentation = (request: PanelPresentation) => {
    if (!embedded && !developer && !sideBySide()) setChatCollapsed(true);
    go({ kind: "panel", key: request.panel_key, context: request.context });
  };

  // A card's action is a hash, because a card should not have to know the shell
  // has a Route type. Same road as `navigate`, entered from the other end.
  const goHash = useCallback((hash: string, replace = false) => go(routeFromHash(hash), replace, hash), [go]);

  const saveDeveloper = (next: boolean) => {
    setDeveloper(next);
    api.savePrefs({ developer: next }).catch(() => {});
    // Turning it off while standing on one of its pages is the same question as
    // arriving on one with it already off, and the effect above answers both.
  };

  const mainPanels = useMemo(
    () => panels.filter((p) => p.slot === "main"),
    [panels],
  );
  /** The name the navigation uses, which is the name the bar addresses. */
  const agentName = directoryRow?.name || "创作助手";
  const queueAvailable = Boolean(boot?.message_delivery || directoryRow?.message_delivery);
  const waitingComposer = queueAvailable ? <Composer sessionId={sessionId} connected={false}
    queueAvailable onToast={onToast} origin={messageOrigin(route)} /> : null;
  // What the open work page hands to the bar (`shell/workTools.tsx`), and whether this Agent can add a page.
  const { tools: workTools, set: setWorkTool } = useWorkToolsState();
  const [canAddWorkspace, setCanAddWorkspace] = useState(false);
  useEffect(() => {
    if (embedded || !workReady) return;
    let live = true;
    surfaceRequests.pages().then(result => {
      if (live) { setSurfacePages(result.pages); setCanAddWorkspace(result.authoring?.available === true); }
    })
      .catch(() => { if (live) setCanAddWorkspace(false); });
    return () => { live = false; };
  }, [embedded, workReady, capabilityVersion, panelSignature]);
  const currentPending = useMemo(() => ownerPending(panels, pendingApprovals.pending,
    !workReady || pendingApprovals.error ? "unavailable" : "ready"),
  [panels, pendingApprovals.pending, workReady, pendingApprovals.error]);
  useEffect(() => {
    if (!studioFrame) return;
    parent.postMessage({ type: "pa.studio.state", state: {
      ready: Boolean(bootError || notRunning || !agentUnread),
      hash: routeToHash(route),
      panelKey: route.kind === "panel" ? route.key : undefined,
      sessionId, panels: navigationPanels, version: `${capabilityVersion}:${panelSignature}`, pendingCount,
      pending: currentPending,
    } }, location.origin);
  }, [studioFrame, bootError, notRunning, agentUnread, route, sessionId, navigationPanels, capabilityVersion, panelSignature, pendingCount, currentPending]);
  // Before the error page and loading feedback: both of those are about a read
  // that has not finished, and this one has finished -- somewhere else.
  const agentStarted = useCallback((started: HubAgent, failure?: unknown) => {
    setAgents(list => list ? list.map(agent => (agent.id === started.id ? started : agent)) : list);
    setHostAgent(current => current?.id === started.id
      && (typeof current.revision !== "number" || typeof started.revision !== "number" || started.revision >= current.revision)
      ? started : current);
    setBootError(null);
    setStartError(failure ?? null);
  }, []);

  if (notRunning && directoryRow) {
    return (
      <LoadingShell label="这个 IP 没有在运行" accountName={account?.name} embedded={embedded || studioFrame}
        agents={agents} currentAgent={AGENT_ID}
        appearance={appearance ? { value: theme, onChange: appearance.onChange } : undefined}>
        <StoppedAgent agent={directoryRow} onStarted={agentStarted} />
        {waitingComposer}
      </LoadingShell>
    );
  }
  if (bootError && !boot) {
    return (
      <LoadingShell label="正在打开 Agent" accountName={account?.name} embedded={embedded || studioFrame}
        appearance={appearance ? { value: theme, onChange: appearance.onChange } : undefined}>
        {AGENT_ID ? (
          <a className="agent-global-back" href="/#/agents">
            <Icon name="back" />
            返回我的 Agent
          </a>
        ) : null}
        <ErrorState error={bootError} onRetry={() => { void load(); }} />
        {waitingComposer}
      </LoadingShell>
    );
  }
  if (agentUnread) {
    return (
      <LoadingShell label={wakesOnDemand(directoryRow) && !directoryRow?.online ? "正在唤醒 Agent"
        : owner?.present === null ? "正在恢复这个 IP 的归属" : "正在读取这个 IP 的工作区"}
        accountName={account?.name} embedded={embedded || studioFrame}>
        {startError || bootError ? <><ErrorState error={startError || bootError} />{waitingComposer}</> : waitingComposer}
      </LoadingShell>
    );
  }

  // With no owner there is nothing else to look at: no principal to attribute a
  // turn to, and no policy to ask.  Better to draw the one thing to be done
  // than a set of empty tables pretending to be normal.
  if (owner.present === false) {
    return (
      <div className={`app app-setup${embedded ? " app-embedded" : studioFrame ? " app-studio-frame" : " app-setup-owner"}`}>
        {!embedded && !studioFrame ? <OwnerNavigation agents={agents} accountName={account?.name || "你"} /> : null}
        <div className="body">
          {!studioFrame ? <header className="top">
            {AGENT_ID ? (
              <a className="agent-global-back" href="/#/agents">
                <Icon name="back" />
                我的 Agent
              </a>
            ) : null}
            <ConnectionPill connection={connection} browserLink={browserLink} agent={directoryRow} />
            <span className="micro top-meta">待认领</span>
            <ThemeSwitch theme={theme} onPick={theme => appearance?.onChange(theme)} settingsHref={account ? undefined : connectionHref(APPEARANCE_SETTING)} />
          </header> : null}
          {connection.state !== "ready" ? (
            <div className="banner banner-critical" role="alert">
              <strong>还没有连上 Agent。</strong>
              连接恢复后才能完成认领。页面会自动重试。
            </div>
          ) : null}
          <main className="main" ref={headingRef}>
            <SetupPage panels={panels} onToast={onToast} connected={connection.state === "ready" && owner.present !== null} />
          </main>
        </div>
        <Toast toast={toast} onDismiss={dismissToast} />
      </div>
    );
  }

  const workPages = workspaceEntries(navigationPanels.filter(panel => panel.slot === "main" && panel.audience === "owner"));
  const openPage = route.kind === "panel" ? navigationPanels.find(panel => panel.key === route.key) : undefined;
  const openEntry = openPage ? workPages.find(page => sameWorkspace(page, openPage)) : undefined;
  const currentPageLabel = workReady ? screenPageLabel(screenViews) : undefined;
  const returnToWork = (destination: Route) => {
    if (!sideBySide()) setChatCollapsed(true);
    navigate(destination);
  };
  const conversationBack = !embedded && !developer && (chatCollapsed || route.kind === "connections") && route.kind !== "chat";

  return (
    <div
      className={
        "app" +
        (embedded ? " app-embedded" : "") +
        (studioFrame ? " app-studio-frame" : "") +
        (settingsEmbedded ? " app-settings-embedded" : "") +
        (route.kind === "today" || route.kind === "approvals" || route.kind === "identity" ? " app-overview" : "") +
        (route.kind === "connections" ? " app-settings" : "") +
        (developer ? " app-developer" : "") +
        (!embedded && !developer ? " app-with-conversation" : "")
      }
    >
      {!embedded && !studioFrame ? <OwnerNavigation agents={agents} currentAgent={AGENT_ID}
        currentPanel={route.kind === "panel" ? route.key : undefined}
        currentPending={currentPending}
        currentRoute={routeToHash(route)}
        active={ownerDestination(route.kind, Boolean(AGENT_ID) || agents !== null)}
        sessionId={sessionId} version={`${capabilityVersion}:${panelSignature}`} activePanels={navigationPanels}
        accountName={account?.name || (AGENT_ID ? "账号信息未读到" : owner.display_name || "本机助手")}
        connected={workReady} onNavigate={goHash} /> : null}

      <div className="body">

        {runtimeNotice && directoryRow ? <div className="banner" role="status">
          <strong>{instanceStatus(directoryRow).label}</strong>{" "}
          {ownerDisabled ? <>已有内容保留。<AgentStartButton agent={directoryRow} onStarted={agentStarted} /></>
            : starting ? "连接后可继续，已有工作和草稿会保留。"
              : "当前显示上次读取的内容；打开工作或发送消息时可继续。"}
        </div> : null}
        {ownerRecovering && !runtimeNotice ? <div className="banner workspace-recovery" role="status">
          正在恢复连接…
        </div> : null}
        {!runtimeNotice && (connection.state !== "ready" || directoryRow?.online === false) ? (
          <div className="banner banner-critical" role="alert">
            <strong>IP 暂未连接</strong>
            {/* The machine's own sentence is the developer view's business: it
                names a socket file and a Python exception. What an owner needs
                is what is true of the page in front of them. */}
            {developer ? (
              <span title={`端点文件：${connection.endpoint}`}>
                {connection.error ?? "workbench 现在没有到核的连接。"}
              </span>
            ) : null}{" "}
            当前显示上次保存的内容，连接恢复后可继续操作。
            {connection.retry_attempts > 0 ? (
              <span className="banner-retry" data-testid="retry-state">
                　正在重新连接 · 第 {connection.retry_attempts} 次
              </span>
            ) : null}
          </div>
        ) : null}

        {!browserLink && !runtimeNotice ? (
          <div className="banner banner-warning" role="alert">
            <strong>实时更新已暂停</strong>
            正在重新连接。
          </div>
        ) : null}

        {spend ? (
          <div
            className={`banner banner-${spend.level === "over" ? "critical" : "warning"}`}
            role="alert"
            data-testid="spend-banner"
          >
            <strong>
              {spend.level === "over"
                ? "已超过本月用量提醒额度"
                : "即将达到本月用量提醒额度"}
            </strong>
            已结算 {formatMoney({ amount: spend.settled, currency: spend.currency })}，另有{" "}
            {formatMoney({ amount: spend.reserved, currency: spend.currency })} 预留还没结算；你设的线是{" "}
            {formatMoney({ amount: spend.limit, currency: spend.currency })}。
          </div>
        ) : null}

        {/* Every panel the shell draws is inside this element, so this is
            where a surface learns that its own files moved (§7.130 phase 2). */}
        <SurfaceConversationFocus.Provider value={focusConversation}><SurfaceIntentComposer.Provider value={placeSurfaceIntent}>
        {/* Whether the machine's own sentences may be drawn on the pages below
            -- the same switch, read by anything that would otherwise put a
            server's wording in front of an owner (D297). */}
        <DeveloperView.Provider value={developer}>
        {/* The settings directory, for the cards that carry a namespace and no
            name for it (`connections/context.tsx`). */}
        <SettingsDirectory.Provider value={settingsDirectory}>
        <PanelDeveloper.Provider value={developer}>
        {/* What draws a plugin's card in the conversation, wherever the
            conversation is mounted (`panel/records.tsx`). */}
        <RecordPanels.Provider value={recordPanels}>
          <SurfaceVersions.Provider value={surfaceVersions}>
            <WorkToolsProvider value={setWorkTool}>
            <SurfaceVisibility.Provider value={workspaceVisible && (developer || workReady)}>
              <div ref={chatLayout.ref} data-chat-position={chatLayout.position} data-resizing={chatLayout.dragging || undefined} data-floating-gesture={chatLayout.gestureKind} style={{ "--chat-width": `${chatLayout.width}px`, "--chat-height": `${chatLayout.height}px`, "--chat-float-x": `${chatLayout.floating.x}px`, "--chat-float-y": `${chatLayout.floating.y}px`, "--chat-float-width": `${chatLayout.floating.width}px`, "--chat-float-height": `${chatLayout.floating.height}px` } as CSSProperties} className={`agent-desk${!embedded && !developer && route.kind === "chat" ? " conversation-only" : ""}${chatCollapsed && route.kind !== "chat" ? " conversation-collapsed" : ""}`}>
                {!embedded ? <AgentBar
                  agents={agents} current={AGENT_ID} name={agentName}
                  status={connectionStatus(connection, browserLink, directoryRow)}
                  navigationKey={`${routeToHash(route)}:${navigationVersion}`} visible={workspaceVisible}
                  title={openPage ? workspaceTitle(openEntry ?? openPage) : undefined}
                  workspaces={openEntry ? workPages.map(page => ({ key: page.key, title: workspaceTitle(page), href: workspaceHref(page, sessionId) })) : []}
                  activeWorkspace={openEntry?.key}
                  tools={workTools} pending={currentPending.count ?? 0}
                  onApprovals={() => goHash(currentPending.href || `#/chat?${new URLSearchParams({ session: sessionId })}`)}
                  onSettings={() => navigate({ kind: "connections" })}
                  createHref={canAddWorkspace ? `#/workspace?${new URLSearchParams({ create: "1", session: sessionId })}` : undefined}
                  onOpenConversation={conversationBack ? (chatCollapsed && route.kind !== "connections" ? focusConversation : () => expandConversation()) : undefined}
                  navButton={outerNavButton}
                  extra={!studioFrame ? <ThemeSwitch theme={theme} onPick={next => appearance?.onChange(next)} settingsHref={account ? undefined : connectionHref(APPEARANCE_SETTING)} /> : null}
                  meta={developer ? <span className="micro top-meta" data-testid="tail-meta">
                    游标 <code className="mono">{connection.cursor ?? "0"}</code> · 已看过 <span className="num">{connection.events_seen}</span> 条
                  </span> : null} /> : null}
                <main
                  className={`main${OWNER_ROUTES.has(route.kind) ? " main-own" : ""}`}
                  ref={headingRef}
                  hidden={runtimePaused || (!embedded && !developer && route.kind === "chat")}
                >
                  <MainPanelPresence.Provider value={mainPanelsPresent.report}>
                  <MainPanelVisibility.Provider value={workspaceExpanded}>
                  {embedded && !settingsEmbedded ? (
                    <AgentWorkspace
                      panels={mainPanels}
                      version={chatVersion + ledgerVersion}
                      chatVersion={chatVersion}
                      registryVersion={capabilityVersion}
                      connected={workReady}
                      queueAvailable={queueAvailable}
                      onToast={onToast}
                      sessionId={sessionId}
                      panelKey={route.kind === "panel" ? route.key : undefined}
                      context={route.kind === "panel" ? route.entry?.context ?? route.context : undefined}
                      createRequest={route.kind === "workspace" && route.create}
                      navigationVisit={route.kind === "panel" ? route.entry?.visit : undefined}
                      onContextChange={rememberPanelContext}
                      incomingSurfaceIntent={surfaceIntent}
                      onSurfaceIntentPlaced={surfaceIntentPlaced}
                    />
                  ) : route.kind === "connections" ? (
                    <ConnectionsPage
                      openingRead={openingSettings}
                      renderServiceSource={settingsEmbedded && AGENT_ID ? (namespace, settingTitle, namespaces) =>
                        <ModelSourceSettings key={namespace} agentId={AGENT_ID!} namespace={namespace} namespaces={namespaces} settingTitle={settingTitle} online={workReady} /> : undefined}
                      panels={panels}
                      embedded={settingsEmbedded}
                      workbench={{ appearance: !account, developer, onDeveloper: saveDeveloper, problems }}
                      active={workspaceVisible && workReady}
                      version={capabilityVersion}
                      onToast={onToast}
                      sessionId={sessionId}
                    />
                  ) : route.kind === "workspace" ? (
                    <SurfaceWorkspace
                      sessionId={sessionId}
                      createRequest={route.kind === "workspace" && route.create}
                      active={workspaceVisible && workReady}
                      version={`${capabilityVersion}:${mainPanels.map((panel) => panel.key).join(",")}`}
                      onToast={onToast}
                      onOpenChat={focusConversation}
                    />
                  ) : route.kind === "today" ? (
                    <AgentHome panels={navigationPanels} agentName={agentName} sessionId={sessionId}
                      onGo={goHash} onFocusConversation={focusConversation} seatKnown={!ownerRecovering} active={workspaceVisible && workReady} />
                  ) : route.kind === "identity" ? (
                    <IdentityPage
                      version={ledgerVersion}
                      owner={owner}
                      principals={boot.principals}
                      accountName={account?.name}
                      onGo={goHash}
                    />
                  ) : route.kind === "chat" ? (developer ? (
                    <ChatPage
                      sessionId={sessionId}
                      onPickSession={id => { setSessionId(id); navigate({ kind: "chat", session: id }); }}
                      // Its own frames move it (`chatFeed`); this moves after a
                      // gap in the stream, here as in the Owner's dock.
                      chatVersion={chatVersion}
                      registryVersion={capabilityVersion}
                      onToast={onToast}
                      connected={developer ? connection.state === "ready" : workReady}
                      queueAvailable={queueAvailable}
                      ownerMode={!developer}
                      origin={messageOrigin(route)}
                      agentName={agentName}
                      incomingSurfaceIntent={surfaceIntent}
                      onSurfaceIntentPlaced={surfaceIntentPlaced}
                      panels={panels}
                    />
                  ) : null) : route.kind === "timeline" ? (
                    <TimelinePage
                      version={ledgerVersion}
                      recordPanels={recordPanels}
                    />
                  ) : route.kind === "approvals" ? (
                    <ApprovalsPage
                      version={approvalVersion}
                      onToast={onToast}
                      ownerMode={!developer}
                    />
                  ) : route.kind === "capabilities" ? (
                    <CapabilitiesPage version={capabilityVersion} />

                  ) : route.kind === "ledger" ? (
                    <LedgerPage name={route.name} version={ledgerVersion} />
                  ) : route.kind === "missing" ? (
                    <div className="agent-home-empty"><h1>页面不存在</h1><button className="btn" onClick={() => navigate({ kind: "today" })}>返回工作区</button></div>
                  ) : panels.some(panel => panel.key === route.key) ? (
                      <PanelView
                        key={`${route.key}:${panels.find(panel => panel.key === route.key)?.generation_id}:${route.entry?.visit ?? 0}`}
                        panelKey={route.key}
                        onToast={onToast}
                        context={route.entry?.context ?? route.context ?? { session_id: sessionId }}
                        panels={panels}
                        seatKnown={workReady}
                        onContextChange={context => rememberPanelContext(route.key, context)}
                        showBackLink
                        toolsInBar
                        /* A panel that did not declare itself the owner's page is
                           not a place an owner can be, so the address goes back
                           to 今天 -- the same replaceState the shell's own
                           developer routes get above (D786). The panel's own
                           declaration decides; there is no list of panels here. */
                        onNotForOwner={() => go({ kind: "today" }, true)}
                        onAnswered={() => { answeredHere.current = route.key; }}
                      />
                  ) : requestEnded ? <RequestSent answered={answeredHere.current === routeKey} conversation={requestReadable.current}
                      onContinue={() => navigate(requestReadable.current ? { kind: "chat", session: requestSession } : { kind: "today" })} />
                  /* The shell cannot know why a page is gone: an answered request and a withdrawn capability look the same
                     from here.  It says what it knows (the page is closed), the two likely reasons, and the way back. */
                  : <div role="status" className="agent-home-empty page-closed"><h1>这个页面已经关闭</h1><p>可能是已经处理完了，也可能是提供它的能力停用了。</p><button className="btn" onClick={() => navigate({ kind: "today" })}>返回工作区</button></div>}
                  </MainPanelVisibility.Provider></MainPanelPresence.Provider>
                  {/* The Hub's settings frame is a row of its own page and has no place to stack one more: a
                      request waits where the conversation is, as a presented page already does. Elsewhere the
                      rows stand above the page (`parallel.css`), not beside it. */}
                  {(embedded || developer) && !settingsEmbedded ? <div className="interaction-rows">
                    <InteractionShelf panels={panels} sessionId={sessionId} onToast={onToast}
                      currentPanel={route.kind === "panel" ? route.key : undefined} seatKnown={workReady} /></div> : null}
                  {/* A settings page -- the Hub's 设置 embeds this one -- is
                      where the Owner configures, not where work is shown. */}
                  {(embedded || developer) && !settingsEmbedded && route.kind !== "connections"
                    ? <PresentationShelf requests={presentations} panels={panels} sessionId={sessionId}
                      currentPanels={mainPanelsPresent.panels} onToast={onToast} seatKnown={workReady} onOpen={openPresentation} /> : null}
                </main>
                {!embedded && !developer && route.kind !== "chat" && route.kind !== "connections" && !chatCollapsed && chatLayout.position !== "floating"
                  ? <div className="conversation-divider" role="separator" aria-label={chatLayout.vertical ? "调整对话高度" : "调整对话宽度"} aria-orientation={chatLayout.vertical ? "horizontal" : "vertical"} aria-valuemin={chatLayout.min} aria-valuemax={chatLayout.max} aria-valuenow={chatLayout.size} tabIndex={0}
                    {...chatLayout.dividerProps} /> : null}
                {!embedded && !developer ? <OwnerConversationDock
                  position={chatLayout.position} onPositionChange={chatLayout.setPosition}
                  floatingMoveProps={chatLayout.moveProps} floatingResizeProps={chatLayout.resizeProps}
                  opening={opening}
                  collapsed={chatCollapsed && route.kind !== "chat"}
                  onCollapse={() => setChatCollapsed(true)}
                  interactions={<><InteractionShelf panels={panels} sessionId={sessionId} onToast={onToast}
                    currentPanel={route.kind === "panel" ? route.key : undefined} seatKnown={workReady} presentation="conversation" />
                    <PresentationShelf requests={presentations} panels={panels} sessionId={sessionId}
                      currentPanels={mainPanelsPresent.panels} onToast={onToast} seatKnown={workReady} onOpen={openPresentation} /></>}
                  containerRef={conversationRef} fullPage={route.kind === "chat"}
                  hidden={route.kind === "connections"}
                  sessionId={sessionId} onPickSession={setSessionId}
                  agentName={agentName} chatVersion={chatVersion} registryVersion={capabilityVersion}
                  pendingApprovals={pendingApprovals.pending ?? []}
                  connected={workReady} ownerMode onToast={onToast} origin={messageOrigin(route)}
                  queueAvailable={queueAvailable}
                  panels={panels} incomingSurfaceIntent={surfaceIntent} onSurfaceIntentPlaced={surfaceIntentPlaced}
                  viewingWork={currentPageLabel}
                  onExpand={() => expandConversation()}
                  onReturn={() => returnToWork(conversationReturn.current)}
                  returnLabel={conversationReturn.current.kind === "connections" ? "返回设置" : "返回工作界面"}
                /> : null}
              </div>
            </SurfaceVisibility.Provider>
            </WorkToolsProvider>
          </SurfaceVersions.Provider>
        </RecordPanels.Provider>
        </PanelDeveloper.Provider>
        </SettingsDirectory.Provider>
        </DeveloperView.Provider>
        </SurfaceIntentComposer.Provider></SurfaceConversationFocus.Provider>


      </div>

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}

/**
 * Whether the Agent is there, in the Owner's words.
 * The Hub's lifecycle row is authoritative when present. Transport failure alone cannot say that the Owner
 * stopped the Agent or that it went to sleep, nor can a cached ready connection override either state.
 */
function connectionStatus(connection: Connection, browserLink: boolean, agent?: HubAgent | null): { tone: StatusTone; label: string; title?: string } {
  if (agent && (agent.desired_running === false || !agent.online)) return instanceStatus(agent);
  if (!browserLink) return { tone: "warning", label: "连接中断", title: "页面暂时收不到新消息" };
  if (connection.state === "ready")
    return { tone: "good", label: "在线" };
  return connection.state === "connecting" ? { tone: "warning", label: "正在连接" } : { tone: "critical", label: "未连接" };
}

function ConnectionPill({
  connection,
  browserLink,
  agent,
}: {
  connection: Connection;
  browserLink: boolean;
  agent?: HubAgent | null;
}) {
  return (
    <div
      className="conn"
      data-testid="connection-pill"
      data-state={connection.state}
    >
      <StatusPill {...connectionStatus(connection, browserLink, agent)} />
    </div>
  );
}
